/**
 * Guest-mode helpers — lightweight first-time-visitor onboarding.
 *
 * Flow:
 *  1. Visitor clicks "Try iAriya as a guest" on /login
 *  2. `startGuestMode()` persists a localStorage flag + a 24h expiry
 *  3. Visitor lands on /venues (or wherever passed in); the sticky
 *     `<GuestModeBanner/>` stays visible across pages prompting sign-up
 *  4. Mode auto-clears when the user signs up, logs in, or the 24h expires
 *
 * No backend involvement — iAriya already serves every browsable listing
 * without auth. Guest mode is purely a UX nudge + conversion banner.
 */

const FLAG_KEY = 'iariya_guest_mode';
const EXPIRES_KEY = 'iariya_guest_mode_expires_at';
const TTL_MS = 24 * 60 * 60 * 1000;

export function startGuestMode() {
  try {
    localStorage.setItem(FLAG_KEY, '1');
    localStorage.setItem(EXPIRES_KEY, String(Date.now() + TTL_MS));
  } catch {
    // ignore — incognito / quota-exceeded
  }
}

export function stopGuestMode() {
  try {
    localStorage.removeItem(FLAG_KEY);
    localStorage.removeItem(EXPIRES_KEY);
  } catch {
    // ignore
  }
}

export function isGuestMode() {
  try {
    if (localStorage.getItem(FLAG_KEY) !== '1') return false;
    const expires = parseInt(localStorage.getItem(EXPIRES_KEY) || '0', 10);
    if (!expires || Date.now() > expires) {
      stopGuestMode();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function guestMinutesRemaining() {
  try {
    const expires = parseInt(localStorage.getItem(EXPIRES_KEY) || '0', 10);
    if (!expires) return 0;
    const diff = expires - Date.now();
    return diff > 0 ? Math.ceil(diff / 60000) : 0;
  } catch {
    return 0;
  }
}
